import SaveOutlined from "@mui/icons-material/SaveOutlined";
import UndoOutlined from "@mui/icons-material/UndoOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { blue } from "@mui/material/colors";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import React, { useMemo, useState } from "react";
import { Form, FormSpy, useFormState } from "react-final-form";
import RffConditional from "../finalForm/shared/RffConditional";
import RffFieldComponentRenderer from "../finalForm/shared/RffFieldComponentRenderer";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";

const CELL_FONT_SIZE = 12; // Default font size for cell values
const SEPERATOR_COLOR = "#dde0e4"; // Default separator color

const EditableDataRow = ({
  label,
  name,
  type,
  options,
  suppressLabel,
  cellValueSize = 6,
  disabled,
  stacked,
  suppressBottomBorder,
  placeholder,
  grow = 0,
  data,
  onUpdateFn,
  condition,
}) => {
  const rowProps = {
    type,
    name,
    options,
    label,
    suppressLabel,
    cellValueSize,
    disabled,
    placeholder,
    suppressBottomBorder,
    stacked,
    onUpdateFn,
    grow,
    data,
  };

  if (!condition) {
    return <RowContentWrapper {...rowProps} />;
  }

  return (
    <RffConditional condition={condition}>
      <RowContentWrapper {...rowProps} />
    </RffConditional>
  );
};

const EditableDataRowFormWrapper = ({
  initialValues = {},
  children,
  grow,
  onSubmit = () => {},
}) => {
  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, form }) => (
        <Box
          component={"form"}
          sx={{ flexGrow: grow }}
          className="editable-field-root"
          onSubmit={handleSubmit}
        >
          {React.Children.map(children, (child) => {
            if (!child) return child;

            return React.cloneElement(child, {
              form,
              initialValues,
            });
          })}
        </Box>
      )}
    />
  );
};

const RowContentWrapper = ({
  data,
  name,
  grow,
  onUpdateFn,
  initialValues,
  ...rest
}) => {
  // Build initialValues for this single field:
  const singleFieldInitialValues = useMemo(() => {
    if (isNil(data)) return {};
    return { [name]: data[name] };
  }, [data, name]);

  return (
    <EditableDataRowFormWrapper
      grow={grow}
      initialValues={singleFieldInitialValues}
    >
      <RowContent name={name} data={data} onUpdateFn={onUpdateFn} {...rest} />
    </EditableDataRowFormWrapper>
  );
};

const RowContent = ({
  label,
  name,
  type,
  options,
  suppressLabel,
  cellValueSize,
  disabled,
  placeholder,
  stacked,
  onUpdateFn,
  loading,
  form,
  initialValues,
}) => {
  const [isFieldDirty, setIsFieldDirty] = React.useState(false);
  const gridRowStyles = useMemo(() => {
    if (stacked) {
      return {};
    }
    return {
      borderBottom: `1px dashed ${SEPERATOR_COLOR}`,
      ".input-type--booleanCheckbox": {
        padding: "0 14px",
      },
      ".input-type--booleanToggle , .input-type--selectToggle": {
        padding: "8.5px 10px",
        ".MuiToggleButtonGroup-root": {
          mt: 0,
        },
      },
    };
  }, [stacked]);

  const handleResetField = React.useCallback(() => {
    form.change(name, initialValues[name]);
  }, [form, name, initialValues]);

  return (
    <>
      <FormSpy
        subscription={{ values: true, dirtyFields: true }}
        onChange={({ dirtyFields, values }) => {
          const dirty = !!(dirtyFields && dirtyFields[name]);
          setIsFieldDirty(dirty);
        }}
      />

      {stacked ? (
        <Flx column>
          <RowLabelCell
            label={label}
            type={type}
            stacked={stacked}
            cellValueSize={cellValueSize}
            suppressLabel={suppressLabel}
          />
          <RowValueCell
            name={name}
            type={type}
            options={options}
            disabled={disabled}
            placeholder={placeholder}
            stacked={stacked}
            cellValueSize={cellValueSize}
            loading={loading}
            onUpdateFn={onUpdateFn}
            fontSize={CELL_FONT_SIZE}
            isDirty={isFieldDirty}
            onResetField={handleResetField}
          />
        </Flx>
      ) : (
        <Grid2
          container
          spacing={2}
          className={`editable-data-row${stacked ? " stacked" : ""}`}
          sx={gridRowStyles}
        >
          <RowLabelCell
            label={label}
            type={type}
            stacked={stacked}
            cellValueSize={cellValueSize}
            suppressLabel={suppressLabel}
          />
          <RowValueCell
            name={name}
            type={type}
            options={options}
            disabled={disabled}
            placeholder={placeholder}
            fontSize={CELL_FONT_SIZE}
            cellValueSize={cellValueSize}
            stacked={stacked}
            loading={loading}
            isDirty={isFieldDirty}
            onUpdateFn={onUpdateFn}
            onResetField={handleResetField}
          />
        </Grid2>
      )}
    </>
  );
};

const RowLabelCell = ({
  label,
  suppressLabel,
  type,
  cellValueSize,
  stacked,
}) => {
  const cellLabelSize = stacked ? 12 : 12 - cellValueSize;
  const cellStyles = useMemo(() => {
    return type === "stringMultiline"
      ? { mt: 1 }
      : { display: "flex", alignItems: "center" };
  }, [type]);
  if (suppressLabel || isNil(label)) return null;
  if (stacked) {
    return (
      <Box className="editable-data-row--label" sx={cellStyles}>
        <Txt sx={{ color: "#33475b", fontSize: CELL_FONT_SIZE }}>{label}</Txt>
      </Box>
    );
  }

  return (
    <Grid2
      size={cellLabelSize}
      className="editable-data-row--label"
      sx={{ py: 0.8, ...cellStyles }}
    >
      <Txt sx={{ color: "#33475b", fontSize: CELL_FONT_SIZE }}>{label}</Txt>
    </Grid2>
  );
};

const RowValueCell = ({
  name,
  type,
  options,
  disabled,
  placeholder,
  stacked,
  cellValueSize,
  loading,
  isDirty,
  onUpdateFn,
  color,
  fontSize,
  onResetField,
}) => {
  const gridRootStyles = useMemo(() => {
    let defaultStyles = {
      position: "relative",
      display: "flex",
      flexGrow: 1,
      flexWrap: "wrap",
      gap: 0.5,
      background: "#e3f2fd35", // blue[50],
      // background: "#e3f2fd4f", // blue[50],
      // borderColor: blue[50],
      border: `1px solid #e3f2fdff`,
      borderBottom: "none",
      // borderRadius: "8px",
      justifyContent: "flex-end",
      fieldset: {
        borderColor: "transparent",
      },
      ".MuiGrid-item": {
        flexGrow: 1,
      },
      ".MuiInputBase-root, .MuiButtonBase-root": {
        fontWeight: 600,
        "&:not(.MuiCheckbox-root)": {
          color,
          fontSize,
          fontWeight: 600,
          "& input::placeholder": {
            fontWeight: "normal !important",
          },
          "& .MuiSelect-select[value='']": {
            fontWeight: 600,
          },
          "& .MuiSelect-select": {
            span: {
              fontWeight: "normal !important",
            },
          },

          "& input::placeholder, & textarea::placeholder": {
            fontWeight: "normal !important",
          },
        },
        "&.Mui-disabled": {
          fontWeight: "600 !important",
          // input: {
          //   "-webkit-text-fill-color": `${color} !important`,
          // },
          "& fieldset": {
            borderColor: "transparent",
          },
          "& input::placeholder, & textarea::placeholder": {
            fontWeight: 600,
          },
          "& .MuiSelect-select[value='']": {
            fontWeight: 600,
          },
        },
      },
      ".MuiToggleButton-root": {
        fontWeight: "normal !important",
        "&.Mui-selected": {
          fontWeight: "600 !important",
        },
      },
    };

    if (!stacked) {
      defaultStyles.alignItems = "center";
    }
    if (stacked) {
      defaultStyles.display = "inline-flex";
      defaultStyles.whiteSpace = "nowrap";
      defaultStyles.width = "fit-content";
      defaultStyles[".MuiFormControl-root"] = {
        width: "fit-content",
      };
      defaultStyles[".MuiOutlinedInput-root"] = {
        width: "auto",
        paddingRight: 0,
      };
      defaultStyles[".MuiOutlinedInput-input"] = {
        width: "auto",
        minWidth: 0,
        minHeight: "unset !important",
        pl: 0.8,
        lineHeight: 1,
      };
      defaultStyles[".MuiInputAdornment-root"] = {
        marginLeft: 0,
      };
    }

    return defaultStyles;
  }, [stacked, color, fontSize]);

  const gridSize = stacked ? 12 : cellValueSize;

  const fieldProps = useMemo(() => {
    return {
      type,
      field: name,
      options,
      disabled,
      placeholder,
    };
  }, [type, name, options, disabled, placeholder]);

  const content = (
    <>
      <RffFieldComponentRenderer field={fieldProps} />
      {isDirty && (
        // <UpdateFieldActions
        //   modified={isDirty}
        //   loading={loading}
        //   stacked={stacked}
        //   onReset={onResetField}
        //   onUpdateFn={onUpdateFn}
        // />
        <UpdateFieldActions
          fieldName={name}
          modified={isDirty}
          loading={loading}
          stacked={stacked}
          onReset={onResetField}
          onUpdateFn={onUpdateFn}
        />
      )}
    </>
  );

  if (stacked) {
    return (
      <Box
        className={`editable-data-row--value input-type--${type}`}
        sx={gridRootStyles}
      >
        {content}
      </Box>
    );
  }

  return (
    <Grid2
      size={gridSize}
      className={`editable-data-row--value input-type--${type}`}
      sx={gridRootStyles}
    >
      {content}
    </Grid2>
  );
};

const UpdateFieldActions = ({
  onUpdateFn,
  modified,
  stacked,
  fieldName,
  onReset,
}) => {
  // const { values } = useFormState();
  // const [loading, setLoading] = useState(false);

  // const onSave = () => {
  //   const field = Object.keys(values).find((key) => values[key] !== undefined);
  //   const newValue = values[field];

  //   if (isFunction(onUpdateFn)) {
  //     setLoading(true);
  //     onUpdateFn({
  //       field,
  //       newValue,
  //       values,
  //       stopLoadingFn: () => {
  //         console.log("stopLoadingFn called");
  //         setLoading(false);
  //       },
  //     });
  //   }
  const { values } = useFormState();
  const [loading, setLoading] = useState(false);

  const onSave = () => {
    let newValue = values[fieldName];

    if (newValue === undefined || newValue === "") {
      newValue = null;
    }

    if (isFunction(onUpdateFn)) {
      setLoading(true);
      onUpdateFn({
        field: fieldName,
        newValue,
        values: { [fieldName]: newValue },
        stopLoadingFn: () => setLoading(false),
      });
    }
  };
  if (!modified) return null;

  if (loading) {
    return (
      <UpdateFieldActionsdWrapper loading stacked={stacked}>
        <Flx
          center
          sx={{
            width: "36px",
            background: "#dcf0ff75",
            // border: "1px solid #a7cce6", // ffebeeff
          }}
        >
          <CircularProgress size={13} />
        </Flx>
      </UpdateFieldActionsdWrapper>
    );
  }
  return (
    <UpdateFieldActionsdWrapper stacked={stacked}>
      <Tooltip title="Undo changes">
        <IconButton
          id="undo"
          size="small"
          onClick={onReset}
          // sx={{ background: red[50] }}
        >
          <UndoOutlined className="thin" color="error" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Save changes">
        <IconButton
          id="save"
          size="small"
          onClick={onSave}
          // sx={{ background: "#e3f2fdcc" }}
        >
          <SaveOutlined className="thin" color="primary" />
        </IconButton>
      </Tooltip>
    </UpdateFieldActionsdWrapper>
  );
};

const UpdateFieldActionsdWrapper = ({ loading, children }) => {
  return (
    <Flx
      center
      sx={{
        background: loading ? blue[50] : "transparent",
        // borderRadius: "8px",
        overflow: "hidden",
        minHeight: "26px",
        flexWrap: "wrap",
        right: 8,
        flexBasis: "70px",
        height: "max-content",
        zIndex: 1,
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        // top: 4,
        ".MuiButtonBase-root": {
          // borderRadius: "0px",
          borderRadius: "8px",
          flexGrow: 1,
          px: 1,
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
          svg: {
            fontSize: "16px !important",
          },
          "&#undo": {
            background: "#ffebee71",
            border: "1px solid #efdee1", // ffebeeff
            borderRight: "none",
            borderTopRightRadius: "0px",
            borderBottomRightRadius: "0px",
            "&:hover": {
              background: "#ffebee",
              border: "1px solid #efdee1",
            },
          },
          "&#save": {
            // background: "#e3f2fdcc",
            background: "#dcf0ff75",
            border: "1px solid #a7cce6", // ffebeeff
            borderLeft: "1px solid #a7cce6c3", // ffebeeff
            // borderLeft: "none",
            borderTopLeftRadius: "0px",
            borderBottomLeftRadius: "0px",
            "&:hover": {
              background: "#dcf0ff",
              border: "1px solid #a7cce6",
            },
          },
        },
      }}
    >
      {children}
    </Flx>
  );
};

export default EditableDataRow;
